import React, { useRef, useState, useEffect } from 'react';

const SignatureCaptureModal = ({ isOpen, onClose, onCapture }) => {
    const canvasRef = useRef(null);
    const [isDrawing, setIsDrawing] = useState(false);
    const [penColor, setPenColor] = useState('#000000');
    const [penWidth, setPenWidth] = useState(2);
    const [isEmpty, setIsEmpty] = useState(true);

    // Nuevos estados para selección de método
    const [captureMethod, setCaptureMethod] = useState('draw'); // 'draw' | 'pad'
    const [padBrand, setPadBrand] = useState('topaz'); // 'topaz' | 'evolis' | 'wacom'
    const [padStatus, setPadStatus] = useState('disconnected'); // 'disconnected' | 'connecting' | 'ready' | 'capturing' | 'success' | 'error'
    const [padSignatureData, setPadSignatureData] = useState(null);

    useEffect(() => {
        if (isOpen && captureMethod === 'draw' && canvasRef.current) {
            const canvas = canvasRef.current;
            const ctx = canvas.getContext('2d');
            ctx.lineCap = 'round';
            ctx.lineJoin = 'round';
            clearCanvas();
        }
    }, [isOpen, captureMethod]);

    const startDrawing = (e) => {
        const { offsetX, offsetY } = getCoordinates(e);
        const ctx = canvasRef.current.getContext('2d');
        ctx.beginPath();
        ctx.moveTo(offsetX, offsetY);
        setIsDrawing(true);
        setIsEmpty(false);
    };

    const draw = (e) => {
        if (!isDrawing) return;
        const { offsetX, offsetY } = getCoordinates(e);
        const ctx = canvasRef.current.getContext('2d');
        ctx.strokeStyle = penColor;
        ctx.lineWidth = penWidth;
        ctx.lineTo(offsetX, offsetY);
        ctx.stroke();
    };

    const stopDrawing = () => {
        if (isDrawing) {
            const ctx = canvasRef.current.getContext('2d');
            ctx.closePath();
            setIsDrawing(false);
        }
    };

    const getCoordinates = (e) => {
        const canvas = canvasRef.current;
        const rect = canvas.getBoundingClientRect();
        let clientX, clientY;

        if (e.touches && e.touches.length > 0) {
            clientX = e.touches[0].clientX;
            clientY = e.touches[0].clientY;
        } else {
            clientX = e.clientX;
            clientY = e.clientY;
        }

        return {
            offsetX: clientX - rect.left,
            offsetY: clientY - rect.top
        };
    };

    const clearCanvas = () => {
        if (canvasRef.current) {
            const canvas = canvasRef.current;
            const ctx = canvas.getContext('2d');
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            setIsEmpty(true);
        }
    };

    // Lógica para Pad de Firmas
    const connectToPad = () => {
        setPadStatus('connecting');
        // Simulación de conexión
        setTimeout(() => {
            setPadStatus('ready');
        }, 1500);
    };

    const startPadCapture = () => {
        setPadStatus('capturing');
        // Simulación de espera de firma del dispositivo
        setTimeout(() => {
            // Aquí iría la lógica real de recepción de datos del pad
            // Simulamos una firma exitosa generando un SVG
            const svgSignature = `
                <svg xmlns="http://www.w3.org/2000/svg" width="300" height="150" viewBox="0 0 300 150">
                    <path d="M50,75 Q100,25 150,75 T250,75" stroke="black" stroke-width="3" fill="none" />
                    <text x="150" y="120" font-family="Arial" font-size="14" text-anchor="middle" fill="gray">Signed via ${padBrand.toUpperCase()}</text>
                </svg>
            `;
            const blob = new Blob([svgSignature], { type: 'image/svg+xml' });
            const url = URL.createObjectURL(blob);
            setPadSignatureData(url);
            setPadStatus('success');
        }, 3000);
    };

    const handleSave = () => {
        if (captureMethod === 'draw' && !isEmpty) {
            const dataUrl = canvasRef.current.toDataURL('image/png');
            onCapture(dataUrl);
            onClose();
        } else if (captureMethod === 'pad' && padSignatureData) {
            // Convertir blob URL a data URL si es necesario
            fetch(padSignatureData)
                .then(r => r.blob())
                .then(blob => {
                    const reader = new FileReader();
                    reader.onload = () => {
                        onCapture(reader.result);
                        onClose();
                    };
                    reader.readAsDataURL(blob);
                });
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-md flex items-center justify-center z-[9999] p-4 font-sans">
            <div className="bg-slate-900 rounded-2xl border border-slate-700 p-6 max-w-2xl w-full shadow-2xl">
                <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center space-x-3">
                        <span className="text-2xl">✍️</span>
                        <h3 className="text-xl font-bold text-white">Captura de Firma Digital</h3>
                    </div>
                    <button onClick={onClose} className="text-slate-400 hover:text-white transition-colors">
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
                    </button>
                </div>

                {/* Selector de Método */}
                <div className="flex space-x-4 mb-6">
                    <button
                        onClick={() => setCaptureMethod('draw')}
                        className={`flex-1 py-3 px-4 rounded-xl border flex items-center justify-center space-x-2 transition-all ${captureMethod === 'draw' ? 'bg-blue-600/20 border-blue-500 text-blue-400' : 'bg-slate-800 border-slate-700 text-slate-400 hover:bg-slate-700'}`}
                    >
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
                        <span className="font-medium">Dibujar en Pantalla</span>
                    </button>
                    <button
                        onClick={() => setCaptureMethod('pad')}
                        className={`flex-1 py-3 px-4 rounded-xl border flex items-center justify-center space-x-2 transition-all ${captureMethod === 'pad' ? 'bg-blue-600/20 border-blue-500 text-blue-400' : 'bg-slate-800 border-slate-700 text-slate-400 hover:bg-slate-700'}`}
                    >
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                        <span className="font-medium">Pad de Firmas</span>
                    </button>
                </div>

                {captureMethod === 'draw' ? (
                    <>
                        <div className="bg-white rounded-xl overflow-hidden mb-4 cursor-crosshair touch-none shadow-inner border-4 border-slate-200">
                            <canvas
                                ref={canvasRef}
                                width={600}
                                height={300}
                                className="w-full h-auto block"
                                onMouseDown={startDrawing}
                                onMouseMove={draw}
                                onMouseUp={stopDrawing}
                                onMouseLeave={stopDrawing}
                                onTouchStart={startDrawing}
                                onTouchMove={draw}
                                onTouchEnd={stopDrawing}
                            />
                        </div>

                        <div className="flex items-center justify-between bg-slate-800 p-3 rounded-lg border border-slate-700 mb-4">
                            <div className="flex items-center space-x-4">
                                <div className="flex flex-col">
                                    <label className="text-xs text-slate-400 mb-1">Color</label>
                                    <input
                                        type="color"
                                        value={penColor}
                                        onChange={(e) => setPenColor(e.target.value)}
                                        className="h-8 w-12 rounded cursor-pointer bg-transparent border-0"
                                    />
                                </div>
                                <div className="flex flex-col w-32">
                                    <label className="text-xs text-slate-400 mb-1">Grosor: {penWidth}px</label>
                                    <input
                                        type="range"
                                        min="1"
                                        max="10"
                                        value={penWidth}
                                        onChange={(e) => setPenWidth(parseInt(e.target.value))}
                                        className="w-full"
                                    />
                                </div>
                            </div>
                            <button
                                onClick={clearCanvas}
                                className="px-3 py-1.5 text-sm text-red-400 hover:text-red-300 hover:bg-red-900/20 rounded transition-colors flex items-center"
                            >
                                <svg className="w-4 h-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                                Limpiar
                            </button>
                        </div>
                    </>
                ) : (
                    <div className="bg-slate-800 rounded-xl p-6 border border-slate-700 mb-4 min-h-[300px] flex flex-col items-center justify-center">
                        {padStatus === 'disconnected' && (
                            <div className="w-full max-w-sm space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-slate-300 mb-2">Seleccione su dispositivo</label>
                                    <select
                                        value={padBrand}
                                        onChange={(e) => setPadBrand(e.target.value)}
                                        className="w-full bg-slate-900 border border-slate-600 rounded-lg px-4 py-3 text-white focus:ring-2 focus:ring-blue-500 outline-none"
                                    >
                                        <option value="topaz">Topaz Systems</option>
                                        <option value="evolis">Evolis SigPad</option>
                                        <option value="wacom">Wacom STU</option>
                                        <option value="generic">Genérico (HID)</option>
                                    </select>
                                </div>
                                <button
                                    onClick={connectToPad}
                                    className="w-full py-3 bg-blue-600 hover:bg-blue-500 text-white rounded-lg font-bold transition-colors shadow-lg shadow-blue-900/30"
                                >
                                    Conectar Dispositivo
                                </button>
                            </div>
                        )}

                        {padStatus === 'connecting' && (
                            <div className="flex flex-col items-center text-slate-300 animate-pulse">
                                <svg className="w-12 h-12 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" /></svg>
                                <p>Buscando dispositivo {padBrand.toUpperCase()}...</p>
                            </div>
                        )}

                        {padStatus === 'ready' && (
                            <div className="flex flex-col items-center w-full">
                                <div className="text-green-400 flex items-center mb-6 bg-green-900/20 px-4 py-2 rounded-full border border-green-500/30">
                                    <div className="w-2 h-2 bg-green-500 rounded-full mr-2 animate-pulse"></div>
                                    <span className="font-medium">Dispositivo Conectado</span>
                                </div>
                                <button
                                    onClick={startPadCapture}
                                    className="px-8 py-4 bg-white text-slate-900 rounded-xl hover:bg-slate-200 transition-all font-bold text-lg shadow-xl flex items-center space-x-3"
                                >
                                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" /></svg>
                                    <span>Iniciar Captura en Pad</span>
                                </button>
                                <p className="mt-4 text-slate-500 text-sm">Firme en el dispositivo físico cuando esté listo</p>
                            </div>
                        )}

                        {padStatus === 'capturing' && (
                            <div className="flex flex-col items-center w-full">
                                <div className="w-full h-48 border-2 border-dashed border-blue-500/50 rounded-xl flex items-center justify-center bg-blue-900/10 mb-4 relative overflow-hidden">
                                    <div className="absolute inset-0 bg-blue-500/5 animate-pulse"></div>
                                    <p className="text-blue-400 font-medium animate-bounce">Esperando firma...</p>
                                </div>
                                <button onClick={() => setPadStatus('ready')} className="text-slate-400 hover:text-white">Cancelar</button>
                            </div>
                        )}

                        {padStatus === 'success' && padSignatureData && (
                            <div className="flex flex-col items-center w-full">
                                <img src={padSignatureData} alt="Firma Capturada" className="h-32 mb-6 bg-white rounded-lg p-2" />
                                <div className="flex space-x-3">
                                    <button onClick={() => setPadStatus('ready')} className="px-4 py-2 border border-slate-600 text-slate-300 rounded-lg hover:bg-slate-800">
                                        Reintentar
                                    </button>
                                    <button onClick={handleSave} className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-500 font-bold shadow-lg shadow-green-900/30">
                                        Aceptar Firma
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                <div className="flex justify-end space-x-3 pt-4 border-t border-slate-800">
                    <button onClick={onClose} className="px-5 py-2 text-slate-300 border border-slate-600 rounded-lg hover:bg-slate-800 transition-colors">
                        Cancelar
                    </button>
                    {captureMethod === 'draw' && (
                        <button
                            onClick={handleSave}
                            disabled={isEmpty}
                            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-bold shadow-lg shadow-blue-900/50 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Insertar Firma
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SignatureCaptureModal;
